/**
 * useNotificationPermission Hook
 *
 * Manages the browser Notification permission lifecycle:
 * 1. On mount, checks if permission is "default" (never asked).
 *    - If autoRequest is true (default): immediately shows the modal prompt.
 *    - If autoRequest is false: waits for the user to trigger it via showModal().
 * 2. If permission is "denied", shows a modal reminder that re-appears after
 *    a configurable cooldown (default: 7 days).
 * 3. If permission is "granted", no prompt is shown.
 * 4. When permission is granted, calls the onGranted callback (if provided)
 *    so the caller can register the FCM token with the backend.
 *
 * Returns:
 *   { permission, showPrompt, dismissPrompt, requestPermission, isPrompting, showModal }
 */

import { useState, useEffect, useCallback, useRef } from 'react';

const LS_KEY = 'd2c_notif_reminder_dismissed_at';
const DEFAULT_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * @param {object} options
 * @param {number} options.cooldownMs - How long after dismissal to re-show the reminder (default 7 days)
 * @param {boolean} options.autoRequest - Whether to auto-show the prompt on mount (default true)
 * @param {function} options.onGranted - Callback invoked when notification permission is granted.
 *        Receives no arguments. Use this to register the FCM token with the backend.
 */
export default function useNotificationPermission(options = {}) {
  const { cooldownMs = DEFAULT_COOLDOWN_MS, autoRequest = true, onGranted } = options;
  const onGrantedRef = useRef(onGranted);
  // Keep the ref in sync so the latest callback is always used
  onGrantedRef.current = onGranted;

  const [permission, setPermission] = useState(() => {
    if (typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission;
  });

  // 'idle' | 'prompting' | 'granted' | 'denied' | 'unsupported'
  const [phase, setPhase] = useState(() => {
    if (typeof Notification === 'undefined') return 'unsupported';
    if (Notification.permission === 'granted') return 'granted';
    return 'idle';
  });

  // Whether the modal is currently visible (manually controlled when autoRequest=false)
  const [modalVisible, setModalVisible] = useState(false);

  // Track whether we've already fired onGranted for an already-granted permission
  // to avoid calling it on every re-render.
  const initialGrantFired = useRef(false);
  // Track the component mount status to avoid calling onGranted after unmount
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Evaluate whether to show the prompt based on current permission state
  const evaluate = useCallback(() => {
    if (typeof Notification === 'undefined') {
      setPhase('unsupported');
      return;
    }

    const perm = Notification.permission;

    if (perm === 'granted') {
      setPhase('granted');
      // Fire onGranted if permission was already granted (e.g., from a previous session)
      // but only once to avoid repeated calls on re-renders or StrictMode double-mounts.
      if (!initialGrantFired.current && typeof onGrantedRef.current === 'function') {
        initialGrantFired.current = true;
        // Use setTimeout to avoid state updates during render phase
        setTimeout(() => {
          if (mountedRef.current) {
            onGrantedRef.current().catch(err => {
              console.error('onGranted callback (already granted) failed:', err);
            });
          }
        }, 0);
      }
      return;
    }

    if (perm === 'denied') {
      // Check cooldown – if user dismissed recently, stay idle
      const dismissedAt = parseInt(localStorage.getItem(LS_KEY), 10);
      if (dismissedAt && Date.now() - dismissedAt < cooldownMs) {
        setPhase('idle');
        return;
      }
      setPhase('denied');
      return;
    }

    // 'default' – never asked
    setPhase('idle');
  }, [cooldownMs]);

  // On mount: evaluate state and auto-show modal if autoRequest is enabled
  useEffect(() => {
    evaluate();

    if (autoRequest) {
      // Auto-show the modal when permission is not yet granted
      const perm = Notification.permission;
      if (perm === 'default' || perm === 'denied') {
        setModalVisible(true);
      }
    }
  }, [evaluate, autoRequest]);

  /**
   * Manually show the prompt modal. Use this when autoRequest is false
   * and you want to trigger the modal from a user action (e.g., a button click).
   */
  const showModal = useCallback(() => {
    setModalVisible(true);
  }, []);

  /**
   * Show the prompt modal (triggers the browser permission dialog).
   * Call this when the user clicks "Enable Notifications".
   * When permission is granted, the onGranted callback is invoked
   * so the caller can register the FCM token with the backend.
   */
  const requestPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') return 'unsupported';

    setPhase('prompting');

    try {
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result === 'granted') {
        setPhase('granted');
        setModalVisible(false);
        // Fire the onGranted callback so the caller can register the FCM token
        if (typeof onGrantedRef.current === 'function') {
          try {
            await onGrantedRef.current();
          } catch (err) {
            console.error('onGranted callback failed:', err);
          }
        }
      } else if (result === 'denied') {
        setPhase('denied');
      } else {
        // 'default' – user dismissed the browser dialog
        setPhase('idle');
      }

      return result;
    } catch (err) {
      console.error('Failed to request notification permission:', err);
      setPhase('idle');
      return Notification.permission;
    }
  }, []);

  /**
   * Dismiss the prompt/reminder and remember the timestamp so it doesn't
   * re-appear until the cooldown expires.
   */
  const dismissPrompt = useCallback(() => {
    localStorage.setItem(LS_KEY, String(Date.now()));
    setPhase('idle');
    setModalVisible(false);
  }, []);

  // Modal should show when:
  // - modalVisible is true (manually triggered or auto-shown)
  // - AND the phase is not 'granted' or 'unsupported'
  const showPrompt = modalVisible && (phase === 'idle' || phase === 'denied' || phase === 'prompting');
  const isPrompting = phase === 'prompting';

  return {
    permission,
    phase,
    showPrompt,
    isPrompting,
    dismissPrompt,
    requestPermission,
    showModal,
  };
}
